// ============================================================================
// GABARITS D'E-MAIL — les courriers qu'Orgazone envoie en son propre nom.
//
// CE QUE L'ÉCRAN PERMET : relire le texte d'un courrier, le réécrire, et revenir au texte d'origine.
// Ni création ni suppression : un usage a UN gabarit, et il en a toujours un.
//
// ⚠️ 🔴 IL N'Y A PAS DE BIBLIOTHÈQUE ICI, À LA DIFFÉRENCE DU BACK-OFFICE. Là-bas, un commerçant
// CHOISIT un gabarit au moment de composer sa campagne : il lui en faut plusieurs, donc des noms et
// une liste. Ici personne ne choisit — c'est le code qui envoie l'invitation, tout seul. Offrir
// plusieurs gabarits sans mécanisme de sélection en ferait prendre un au hasard.
//
// ⚠️ 🔴 « REVENIR AU TEXTE D'ORIGINE » N'EST PAS UNE SUPPRESSION : le courrier du logiciel reprend sa
// place, et l'écran le MONTRE avant qu'on le fasse. C'est ce qui rend une retouche malheureuse
// réversible sans nous — et le serveur dit si une ligne existait vraiment, pour qu'on n'annonce pas
// une action qui n'a pas eu lieu.
//
// ⚠️ 🔴 UN TEXTE ENREGISTRÉ N'EST PAS FORCÉMENT CELUI QUI PART. Une ligne écrite avant qu'une règle
// existe — ou restaurée d'une sauvegarde — peut ne plus passer la validation : l'envoi retombe alors
// sur le courrier d'origine. Cet écran est le SEUL endroit où cela peut se voir, donc il le dit en
// tête de carte (§23.0).
//
// ⚠️ 🔴 L'IdP N'EST PAS ICI, ET C'EST UNE DÉCISION. Qui contrôle le texte d'un courrier
// d'authentification contrôle la réinitialisation de tous les comptes : ces messages-là restent dans
// le code de l'IdP, où ils ne dépendent d'aucune ligne de base.
//
// ⚠️ AUCUNE TRADUCTION ICI : la Console est en français, comme ses voisins. Les JETONS (`{lien}`),
// eux, ne se traduiraient jamais — leur nom EST la clé que le serveur remplace.
// ============================================================================
import { useCallback, useEffect, useMemo, useState } from 'react';
import { alert, confirm, showToast, EmailEditor, EmptyState, type EmailEditorField } from '@orgazone/ui-web';
import { api, type ConsoleEmailTemplate, type EmailMergeField, type EmailTemplateInput } from '../api';
import { useAuth, isSuperadmin } from '../auth';

/** Le nom d'un usage, tel qu'on le lit — et à quel moment ce courrier part. */
const USAGES: Record<string, { title: string; when: string }> = {
  draft_invite: {
    title: 'Invitation à remplir un dossier',
    when: 'Part quand on émet un lien de dépôt à un prospect. C’est le tout premier message qu’une enseigne reçoit de nous.',
  },
  draft_complement: {
    title: 'Complément demandé',
    when: 'Part quand on renvoie un dossier déjà déposé pour correction. Le motif écrit dans la Console y apparaît.',
  },
  deposit_confirmation: {
    title: 'Accusé de réception d’un dossier',
    when: 'Part au déposant dès que son dossier arrive. Il n’annonce aucun délai — rien n’est créé à ce stade.',
  },
};

/** Ce que l'écran transmet, à partir de ce qu'on a tapé. */
export function corpsDeSaisie(s: {
  subject: string; title: string; body: string; buttonLabel: string; buttonUrl: string;
}): EmailTemplateInput {
  return {
    subject: s.subject,
    // ⚠️ UN TITRE VIDE EST L'ABSENCE DE TITRE : transmis en chaîne vide, il ferait poser un titre
    //    BLANC en tête du message, que le destinataire verrait comme un trou.
    title: s.title.trim() === '' ? null : s.title,
    body: s.body,
    // ⚠️ DEUX CHAMPS VIDES VALENT « PAS DE BOUTON ». Une moitié saisie est transmise TELLE QUELLE :
    //    c'est le serveur qui tranche si c'en est un — décider ici aussi en ferait une seconde
    //    source de vérité, et le contrôle serait inerte puisque le cœur rend déjà la même chose.
    button: s.buttonLabel.trim() === '' && s.buttonUrl.trim() === ''
      ? null : { label: s.buttonLabel, url: s.buttonUrl },
  };
}

/** Ce qu'on annonce après « revenir au texte d'origine » — `false` veut dire qu'il n'y avait plus de ligne. */
export function messageDeRetour(restored: boolean): string {
  return restored
    ? 'Le courrier d’origine est de nouveau celui qui part.'
    : 'Ce gabarit était déjà revenu à son texte d’origine.';
}

/** L'état d'un usage, en une phrase — c'est elle que l'éditeur lit avant de toucher au texte. */
export function etatDe(t: Pick<ConsoleEmailTemplate, 'custom' | 'rejected' | 'updatedAt' | 'updatedBy'>): {
  ton: 'ok' | 'warn' | 'err'; texte: string;
} {
  // ⚠️ 🔴 L'ORDRE COMPTE : un texte ÉCARTÉ passe avant « personnalisé ». Les deux sont vrais à la
  //    fois — une ligne existe, et elle ne sert pas —, et c'est le second fait qui importe.
  if (t.rejected) return { ton: 'err', texte: `Ce texte n’est PAS celui qui part : ${t.rejected}` };
  if (!t.custom) return { ton: 'ok', texte: 'Texte d’origine du logiciel.' };
  // ⚠️ AUCUNE ÉTIQUETTE DE LANGUE ÉCRITE ICI : `undefined` laisse le navigateur de l'opérateur
  //    décider. Cette date dit QUAND quelqu'un a retouché le texte — rien qui doive s'afficher à la
  //    française pour un opérateur qui ne l'est pas. (Les voisins de cet écran portent encore
  //    « fr-FR » ; c'est la dette que le plafond de l'audit fait décroître, pas un modèle à suivre.)
  const quand = t.updatedAt ? new Date(t.updatedAt).toLocaleString(undefined, { dateStyle: 'long', timeStyle: 'short' }) : null;
  return {
    ton: 'warn',
    texte: `Texte personnalisé${quand ? `, modifié le ${quand}` : ''}${t.updatedBy ? ` par ${t.updatedBy}` : ''}.`,
  };
}

/** ⚠️ Les noms viennent du thème de la Console (`wa` = avertissement, `da` = danger) — pas inventés. */
const TONS: Record<'ok' | 'warn' | 'err', { bg: string; fg: string }> = {
  ok: { bg: 'var(--ok-bg)', fg: 'var(--ok-fg)' },
  warn: { bg: 'var(--wa-bg)', fg: 'var(--wa-fg)' },
  err: { bg: 'var(--da-bg)', fg: 'var(--da-fg)' },
};

interface Saisie { subject: string; title: string; body: string; buttonLabel: string; buttonUrl: string }

const saisieDe = (t: EmailTemplateInput): Saisie => ({
  subject: t.subject, title: t.title ?? '', body: t.body,
  buttonLabel: t.button?.label ?? '', buttonUrl: t.button?.url ?? '',
});

export function GabaritsEmail() {
  const { user } = useAuth();
  const sa = isSuperadmin(user);
  const [templates, setTemplates] = useState<ConsoleEmailTemplate[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [ouvert, setOuvert] = useState<string | null>(null);
  const [saisie, setSaisie] = useState<Saisie | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setTemplates((await api.consoleEmailTemplates()).templates); setErreur(null); }
    // ⚠️ LE MOTIF RÉEL, jamais une liste vide : un écran vide se lit comme « aucun gabarit », ce qui
    //    est faux — il y en a toujours trois.
    catch (e) { setErreur((e as Error).message); setTemplates(null); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const courant = useMemo(() => templates?.find((t) => t.purpose === ouvert) ?? null, [templates, ouvert]);

  const ouvrir = (t: ConsoleEmailTemplate) => { setOuvert(t.purpose); setSaisie(saisieDe(t)); };

  const enregistrer = async () => {
    if (!courant || !saisie) return;
    setBusy(true);
    try {
      await api.saveConsoleEmailTemplate(courant.purpose, corpsDeSaisie(saisie));
      showToast('Gabarit enregistré.');
      setOuvert(null); setSaisie(null);
      await load();
    } catch (e) {
      // ⚠️ LE MESSAGE DU SERVEUR EST RENDU TEL QUEL : il nomme le champ ou le jeton fautif, et c'est
      //    la seule chose qui permette de corriger sans nous.
      void alert({ title: 'Gabarit refusé', message: (e as Error).message });
    } finally { setBusy(false); }
  };

  const revenir = async (t: ConsoleEmailTemplate) => {
    const ok = await confirm({
      title: `Revenir au texte d’origine de « ${USAGES[t.purpose]?.title ?? t.purpose} » ?`,
      message: 'Votre texte est effacé et le courrier du logiciel reprend sa place. Les messages déjà envoyés ne changent pas.',
      confirmLabel: 'Revenir au texte d’origine',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await api.resetConsoleEmailTemplate(t.purpose);
      showToast(messageDeRetour(r.restored));
      if (ouvert === t.purpose) { setOuvert(null); setSaisie(null); }
      await load();
    } catch (e) { void alert({ title: 'Action impossible', message: (e as Error).message }); }
    finally { setBusy(false); }
  };

  // ⚠️ LE CONTENEUR ET LE TITRE DE PAGE, COMME TOUS LES ÉCRANS DE LA CONSOLE : sans eux, l'écran n'a ni marges ni titre.
  //    Le titre est celui du rail ; la phrase dit ce que l'écran règle.
  const head = (
    <div style={{ marginBottom: 16 }}>
      <h1 className="page-title">Gabarits d’e-mail</h1>
      <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink500)', marginTop: 3, maxWidth: '52rem' }}>
        Les courriers qu’Orgazone envoie en son propre nom. Un usage a un seul texte : le modifier
        remplace le courrier du logiciel, revenir en arrière le restaure.
        {' '}Les courriers d’authentification de l’IdP ne sont pas ici — ils restent dans son code.
      </div>
    </div>
  );
  if (erreur) {
    return <div className="page">{head}<EmptyState icon="mail" title="Gabarits illisibles" note={erreur} /></div>;
  }
  if (!templates) return <div className="page">{head}<div className="muted">Chargement des gabarits…</div></div>;

  return (
    <div className="page">
      {head}
      <div style={{ display: 'grid', gap: 14 }}>

        {templates.map((t) => {
          const usage = USAGES[t.purpose];
          const etat = etatDe(t);
          const estOuvert = ouvert === t.purpose;
          return (
            <div key={t.purpose} className="dcard" style={{ padding: 14, display: 'grid', gap: 10 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 22rem', minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{usage?.title ?? t.purpose}</div>
                  <div style={{ opacity: .7, fontSize: 'var(--t-lead)', marginTop: 2 }}>{usage?.when ?? ''}</div>
                </div>
                <span style={{
                  background: TONS[etat.ton].bg, color: TONS[etat.ton].fg,
                  borderRadius: 8, padding: '4px 10px', fontSize: 'var(--t-dense)', alignSelf: 'center',
                }}>{etat.texte}</span>
                {sa && (
                  <div style={{ display: 'flex', gap: 8, alignSelf: 'center' }}>
                    <button type="button" disabled={busy} onClick={() => (estOuvert ? setOuvert(null) : ouvrir(t))}>
                      {estOuvert ? 'Fermer' : 'Modifier'}
                    </button>
                    {/* ⚠️ OFFERT SEULEMENT S'IL Y A QUELQUE CHOSE À DÉFAIRE : un bouton qui ne peut rien
                        faire ferait croire qu'on a agi. */}
                    {t.custom && (
                      <button type="button" disabled={busy} onClick={() => void revenir(t)}>Revenir au texte d’origine</button>
                    )}
                  </div>
                )}
              </div>

              {estOuvert && saisie && (
                <EditeurGabarit
                  key={t.purpose}
                  template={t} saisie={saisie} setSaisie={setSaisie}
                  busy={busy} onSave={() => void enregistrer()} onCancel={() => { setOuvert(null); setSaisie(null); }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** L'explication d'un champ au survol de sa pastille : ce qu'il vaut, s'il est exigé, et ce qui part quand il manque. */
export function fieldTip(f: EmailMergeField): string {
  const head = f.required ? `${f.description} — obligatoire` : f.description;
  return `${head} · ${f.fallback === '' ? 'repli : rien' : `repli : « ${f.fallback} »`}`;
}

/** Le formulaire d'un gabarit — objet, titre, corps, bouton, et les jetons que cet usage autorise. */
function EditeurGabarit({ template, saisie, setSaisie, busy, onSave, onCancel }: {
  template: ConsoleEmailTemplate; saisie: Saisie; setSaisie: (s: Saisie) => void;
  busy: boolean; onSave: () => void; onCancel: () => void;
}) {
  // ⚠️ L'obligation et le repli de chaque champ se disent à la pastille : sans eux, l'éditeur découvrait au refus du
  //    serveur qu'un champ était exigé.
  const champs: EmailEditorField[] = useMemo(
    () => template.fields.map((f) => ({ token: `{${f.name}}`, required: f.required === true, tip: fieldTip(f) })),
    [template.fields],
  );
  const maj = (p: Partial<Saisie>) => setSaisie({ ...saisie, ...p });

  /**
   * ⚠️ 🔴 L'ÉDITEUR EST NON CONTRÔLÉ — il est semé UNE FOIS au montage (réécrire son contenu à chaque
   * frappe replacerait le curseur au début). Charger un autre texte demande donc de le REMONTER, et
   * cette graine est la seule façon de le lui dire. Sans elle, « charger le texte d'origine »
   * changerait l'objet et le titre en laissant le corps intact : un bouton qui fait à moitié semblant.
   */
  const [graine, setGraine] = useState(0);
  const chargerOrigine = () => { setSaisie(saisieDe(template.fallback)); setGraine((g) => g + 1); };

  return (
    <div style={{ display: 'grid', gap: 10, borderTop: '1px solid var(--line)', paddingTop: 12 }}>
      <div style={{ display: 'grid', gap: 6 }}>
        <label htmlFor={`obj-${template.purpose}`} style={{ fontSize: 'var(--t-lead)', opacity: .8 }}>Objet du message</label>
        <input id={`obj-${template.purpose}`} value={saisie.subject}
          onChange={(e) => maj({ subject: e.target.value })} />
      </div>
      <div style={{ display: 'grid', gap: 6 }}>
        <label htmlFor={`tit-${template.purpose}`} style={{ fontSize: 'var(--t-lead)', opacity: .8 }}>
          Titre en tête du message <span style={{ opacity: .6 }}>— laissez vide si le corps se suffit</span>
        </label>
        <input id={`tit-${template.purpose}`} value={saisie.title}
          onChange={(e) => maj({ title: e.target.value })} />
      </div>

      {/* ⚠️ LES JETONS SE COPIENT, ILS NE SE TRADUISENT PAS : leur nom EST la clé que le serveur
          remplace. Ceux qui sont OBLIGATOIRES sont dits ici — le serveur refusera sinon, et mieux
          vaut le savoir avant d'écrire. */}
      <div style={{ fontSize: 'var(--t-lead)', opacity: .8 }}>
        Champs disponibles :{' '}
        {template.fields.length === 0
          ? <em>aucun pour ce courrier.</em>
          : template.fields.map((f, i) => (
            <span key={f.name}>
              {i > 0 ? ' · ' : ''}
              <code>{`{${f.name}}`}</code> {f.description}
              {f.required ? <b> (obligatoire)</b> : ''}
            </span>
          ))}
      </div>

      <div style={{ display: 'grid', gap: 6 }}>
        <span style={{ fontSize: 'var(--t-lead)', opacity: .8 }}>Corps du message</span>
        <EmailEditor
          key={graine}
          initialValue={saisie.body}
          onChange={(html) => setSaisie({ ...saisie, body: html })}
          fields={champs}
          ariaLabel={`Corps du courrier « ${USAGES[template.purpose]?.title ?? template.purpose} »`}
          minHeight={220}
        />
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'grid', gap: 6, flex: '1 1 14rem' }}>
          <label htmlFor={`bl-${template.purpose}`} style={{ fontSize: 'var(--t-lead)', opacity: .8 }}>Texte du bouton</label>
          <input id={`bl-${template.purpose}`} value={saisie.buttonLabel}
            onChange={(e) => maj({ buttonLabel: e.target.value })} />
        </div>
        <div style={{ display: 'grid', gap: 6, flex: '1 1 18rem' }}>
          <label htmlFor={`bu-${template.purpose}`} style={{ fontSize: 'var(--t-lead)', opacity: .8 }}>
            Adresse du bouton <span style={{ opacity: .6 }}>— un jeton comme {'{lien}'} est accepté</span>
          </label>
          <input id={`bu-${template.purpose}`} value={saisie.buttonUrl}
            onChange={(e) => maj({ buttonUrl: e.target.value })} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" disabled={busy} onClick={onSave}>Enregistrer</button>
        {/* ⚠️ 🔴 CHARGER LE TEXTE D'ORIGINE SANS RIEN ENREGISTRER : c'est ce qui permet de voir avant
            de décider, et de repartir du vrai texte plutôt que d'en réécrire un de mémoire. Le geste
            ne touche PAS la base — seul « Enregistrer » écrit. */}
        <button type="button" disabled={busy} onClick={chargerOrigine}>
          Charger le texte d’origine
        </button>
        <button type="button" disabled={busy} onClick={onCancel}>Annuler</button>
      </div>
    </div>
  );
}
